/**
 * The family screen (§A.8): procreation odds, readiness checklist, family
 * tree, children with genomes/traits and projections. Reads the OBS-8
 * `family` extension key from every known role and merges them (the
 * mating coordinator spans both parents, so no single role's file is
 * authoritative on its own — see README.md "Snapshot extension contract").
 *
 * design/reshape/RESHAPE.md §1/§3 (AUDIT.md finding 5): the readiness
 * checklist and children grid now sit inside the same `.obs-panel`
 * header/body chrome every other screen uses (was bespoke unwrapped rows),
 * and a readiness row's satisfied/not-met state gets a non-color glyph
 * (✓/✕) in addition to color — the underlying data (readiness rules, gene
 * values, projection notes) is unchanged.
 */

import { loadRoleSnapshot } from "../data/snapshotIndex";
import { resolveAllRoleLocations } from "../data/roleDirectory";
import { renderNotPublishedYet } from "../panels/NotPublishedYet";
import type { FamilyChild, FamilyReadinessFactor } from "../types/snapshot";
import { attachTooltip } from "../ui/tooltip";
import { renderScreenHeader } from "./screenHeader";
import { statLabel } from "../ui/statLabel";
import { formatPercent } from "../format/number";

export class FamilyScreen {
  constructor(private readonly container: HTMLElement) {}

  async mount(): Promise<void> {
    this.container.innerHTML = "";
    const locations = await resolveAllRoleLocations();

    const readiness: (FamilyReadinessFactor & { organismLabel: string })[] = [];
    const childrenById = new Map<string, FamilyChild>();
    let anyPublished = false;
    let procreationProbability: number | null = null;

    for (const location of locations) {
      const snapshot = await loadRoleSnapshot(location);
      if (!snapshot) continue;
      const family = await snapshot.family();
      if (!family) continue;
      anyPublished = true;
      if (family.procreation_probability !== null)
        procreationProbability = family.procreation_probability;
      // `location.displayName` (2026-09-28 fix), not the raw
      // `organismId` — the exact same real, non-fabricated friendly label
      // (`roleLabel(role)`) the fleet bar/fleet cards already show for this
      // organism, not a guessed shortening of the id string. Verified live:
      // this used to read "family-populated-male: Lifecycle stage
      // eligible" instead of "Male · equities: Lifecycle stage eligible".
      for (const factor of family.readiness)
        readiness.push({ ...factor, organismLabel: location.displayName });
      for (const child of family.children) childrenById.set(child.child_id, child);
    }

    if (!anyPublished) {
      this.container.appendChild(
        renderNotPublishedYet(
          "Family",
          "OBS-8's family assessment (readiness, procreation odds, tree, children, " +
            "trait/projection data) has not been published yet.",
        ),
      );
      return;
    }

    this.container.appendChild(
      renderScreenHeader(
        "Family",
        "Procreation readiness, odds, and the family tree — merged across every parent role (no single role's file is authoritative on its own).",
      ),
    );

    const summary = document.createElement("div");
    summary.className = "obs-stat-row";
    const summaryLabel = statLabel("Chance of a new child soon", "P(procreation), horizon");
    attachTooltip(
      summaryLabel,
      "Probability both parents satisfy their readiness gates within the projection horizon.",
    );
    const summaryValue = document.createElement("span");
    summaryValue.textContent =
      procreationProbability !== null ? formatPercent(procreationProbability, 1) : "not modeled";
    summary.appendChild(summaryLabel);
    summary.appendChild(summaryValue);
    this.container.appendChild(summary);

    if (readiness.length > 0) {
      this.container.appendChild(this.renderReadinessPanel(readiness));
    }

    this.container.appendChild(this.renderChildrenPanel(childrenById));
  }

  private renderReadinessPanel(
    readiness: readonly (FamilyReadinessFactor & { organismLabel: string })[],
  ): HTMLDivElement {
    const panel = document.createElement("div");
    panel.className = "obs-panel";
    panel.style.marginTop = "var(--obs-space-4)";

    const header = document.createElement("div");
    header.className = "obs-panel-header";
    const title = document.createElement("span");
    title.className = "obs-panel-title";
    title.textContent = "Readiness checklist";
    attachTooltip(
      title,
      "Each parent's mating-coordinator predicates: lifecycle stage, performance gates, capital reservation, compatibility, cooldowns.",
    );
    header.appendChild(title);
    panel.appendChild(header);

    const body = document.createElement("div");
    body.className = "obs-panel-body";
    body.style.padding = "var(--obs-space-3)";
    for (const factor of readiness) {
      // A left-edge status bar (colour, not just the ✓/✕ prefix) —
      // DIRECTION.md §3: a reader scans the left edge for the one red flag
      // instead of reading every row's text. The glyph stays too — colour
      // is still never the only cue (dataviz skill rule).
      const row = document.createElement("div");
      row.className = `obs-readiness-row ${factor.satisfied ? "obs-readiness-ok" : "obs-readiness-not-met"}`;

      const label = statLabel(`${factor.organismLabel}: ${factor.label}`);

      const value = document.createElement("span");
      value.className = factor.satisfied ? "obs-value-up" : "obs-value-down";
      value.textContent = `${factor.satisfied ? "✓ OK" : "✕ not met"} (${factor.value}, ${factor.rule})`;

      row.appendChild(label);
      row.appendChild(value);
      body.appendChild(row);
    }
    panel.appendChild(body);
    return panel;
  }

  private renderChildrenPanel(childrenById: ReadonlyMap<string, FamilyChild>): HTMLDivElement {
    const panel = document.createElement("div");
    panel.className = "obs-panel";
    panel.style.marginTop = "var(--obs-space-4)";

    const header = document.createElement("div");
    header.className = "obs-panel-header";
    const title = document.createElement("span");
    title.className = "obs-panel-title";
    title.textContent = `Children (${childrenById.size})`;
    attachTooltip(
      title,
      "Each child's genome, incubation result, portfolio composition, and projected performance.",
    );
    header.appendChild(title);
    panel.appendChild(header);

    const body = document.createElement("div");
    body.className = "obs-panel-body";

    if (childrenById.size === 0) {
      body.appendChild(renderNotPublishedYet("Children", "No children have been born yet."));
      panel.appendChild(body);
      return panel;
    }

    const grid = document.createElement("div");
    grid.className = "obs-family-grid";
    for (const child of childrenById.values()) {
      grid.appendChild(this.renderChildCard(child));
    }
    body.appendChild(grid);
    panel.appendChild(body);
    return panel;
  }

  private renderChildCard(child: FamilyChild): HTMLDivElement {
    const card = document.createElement("div");
    card.className = "obs-child-card";
    card.innerHTML = `
        <strong>${escapeHtml(child.child_id)}</strong>
        <div>parents: ${escapeHtml(child.parents[0])} &times; ${escapeHtml(child.parents[1])}</div>
        <div>born: ${child.born_at ? escapeHtml(child.born_at) : "pending"}</div>
        <table class="obs-table">
          <thead><tr><th>gene</th><th>value</th><th>origin</th></tr></thead>
          <tbody>${child.genome
            .map(
              (gene) =>
                `<tr><td>${escapeHtml(gene.gene)}</td><td>${gene.value.toFixed(3)}</td><td>${escapeHtml(gene.origin_parent ?? "unknown")}</td></tr>`,
            )
            .join("")}</tbody>
        </table>
        <div>${escapeHtml(child.projection_note)}</div>
      `;
    return card;
  }

  dispose(): void {}
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
